"use client";

import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Monitor, Smartphone, Tablet } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSections } from "@/store/slices/webbuilderSlice";
import { CanvasContent } from "@/components/webbuilder/features/canvas/CanvasContent";
import { usePages } from "@/hooks/usePages";
import { WebBuilderPreloader } from "@/components/webbuilder/ui/loaders/WebBuilderPreloader";
import { ButtonOutline } from "@/components/common/ButtonOutline";

export default function PreviewPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draftId");
  const dispatch = useAppDispatch();
  const { actions } = usePages();
  const currentPage = useAppSelector(
    (state) => state.page?.currentPage || null,
  );
  const [viewportMode, setViewportMode] = useState<
    "desktop" | "tablet" | "mobile"
  >("desktop");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadDraft = async () => {
      if (!draftId) {
        if (active) {
          setError("Missing draftId in preview URL.");
          setLoading(false);
        }
        return;
      }

      if (!currentPage?.id) {
        if (active) {
          setError("Preview requires a persisted page selection.");
          setLoading(false);
        }
        return;
      }

      try {
        const response = await actions.getPageDraft(currentPage.id, draftId);
        const sections = response?.data?.sections || response?.sections || [];
        dispatch(setSections(sections));
      } catch (caughtError) {
        if (active) {
          setError(
            caughtError instanceof Error
              ? caughtError.message
              : "Failed to load draft preview.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDraft();

    return () => {
      active = false;
    };
  }, [actions, currentPage?.id, dispatch, draftId]);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-2xl border border-border bg-white p-6 shadow-xl dark:bg-auth-card">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Preview unavailable</h1>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <WebBuilderPreloader message="Loading draft preview..." />
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background">
      <div className="fixed left-4 top-4 z-50 flex items-center gap-2 rounded-full border border-border bg-white/90 px-3 py-2 shadow-lg backdrop-blur dark:bg-auth-card/90">
        <ButtonOutline
          onClick={() => setViewportMode("desktop")}
          className={`h-9 w-9 p-0 ${viewportMode === "desktop" ? "bg-primary text-primary-foreground" : ""}`}
          aria-label="Desktop preview"
        >
          <Monitor className="h-4 w-4" />
        </ButtonOutline>
        <ButtonOutline
          onClick={() => setViewportMode("tablet")}
          className={`h-9 w-9 p-0 ${viewportMode === "tablet" ? "bg-primary text-primary-foreground" : ""}`}
          aria-label="Tablet preview"
        >
          <Tablet className="h-4 w-4" />
        </ButtonOutline>
        <ButtonOutline
          onClick={() => setViewportMode("mobile")}
          className={`h-9 w-9 p-0 ${viewportMode === "mobile" ? "bg-primary text-primary-foreground" : ""}`}
          aria-label="Mobile preview"
        >
          <Smartphone className="h-4 w-4" />
        </ButtonOutline>
      </div>

      <div className="h-full w-full overflow-auto pt-16">
        <CanvasContent
          currentPage={currentPage}
          storeId={storeId || ""}
          viewportMode={viewportMode}
          showEditorControls={false}
        />
      </div>
    </div>
  );
}
